// ============================================================================
// geometry/grip-plate.js — code-generated tri-grip / change plate (Phase F, product 5)
// ============================================================================
// The one product the V2 plan flags as breaking the clean-disc assumption (§3):
// a flat disc with three hand cutouts and a fixed Olympic bore. Unlike the barbell
// — which really was "a dumbbell with a longer handle" and so delegated wholesale
// to dumbbell.js — this shares no profile with anything else, so it carries its own
// emit machinery, exactly as bumper-plate.js does. That is the house pattern, not
// duplication for its own sake: each product owns the mesh it can't share.
//
// EVERYTHING HERE IS MEASURED, from Ryan's own outline trace (`Grip Plates.webp`,
// which he confirms is the 45 lb) plus his dimension table. Nothing is styled by eye.
//
//   FACE (both ±Z are BRAND faces — the design reads on either side):
//   ├─ bore            r = BORE_RADIUS         fixed 50.4 mm Olympic, ALL weights
//   ├─ collar ring     → COLLAR_RADIUS         metal bushing, absolute (not scaled)
//   ├─ clean centre    → cutIn(θ)              a real, uninterrupted crest field
//   ├─ CUTOUT band     cutIn(θ) → cutOut(θ)    3 × 52°, at 120° — a hole
//   ├─ clean rim       cutOut(θ) → R - er      arc-text land
//   └─ rounded rim     er quarter-round → R
//
// WHY THE PLAN'S WARNING DOESN'T BITE. §3 says "a grip-plate hub may deny
// `clean-center`". On Ryan's actual geometry it doesn't: the collar ends at ~0.148R
// and the cutouts don't start until ~0.599R, leaving a clean centre field of nearly
// half the radius — plenty for a crest — plus a clean rim outside 0.852R. The zone
// descriptor still earns its keep (this is the first product with cutout paths at
// all), but the scary case was hypothetical. Measure before architecting.
//
// THESE SCALE THE OPPOSITE WAY TO EVERY OTHER PRODUCT. The dumbbell and bumper cap
// their DIAMETER and grow thickness; a grip plate holds thickness (25/35/45 are all
// 1.65") and grows diameter. And because the bore is a fixed 50.4 mm, it eats a
// steadily bigger fraction of the face as plates get smaller (0.112R at 45 lb ->
// 0.187R at 10 lb). Nothing here may assume a constant bore RATIO.
//
// THREE MATERIAL SLOTS, mirroring the plate/dumbbell so reveal-3d's material rig
// binds this with no special-casing:
//   0 BrandFace — the two flat faces (planar UV, the design)
//   1 body      — rim wall, rim rounds, and the three cutout walls
//   2 metal     — the collar ring + the bore wall
// (slot 3 `knurl` exists on the dumbbell; a grip plate has none, so it is not emitted)
//
// PURE (no THREE / WebGL) so it is node-testable; the THREE factory takes THREE as an
// argument. Winding is explicit: every quad is emitted with a desired outward normal.
// ============================================================================

/** Inches → metres. Ryan's dims are quoted in inches; the generator works in metres. */
const IN = 0.0254;

/**
 * Olympic bore — 50.4 mm diameter, FIXED at every weight. Not a ratio.
 * Matches `bumper-plate.js`'s COLLAR_R (25.2 in its 450mm viewBox units), and Ryan's
 * trace independently lands within 3.6% of it (0.108 × 225.0 mm = 24.3 mm), which is
 * what confirms his outline is drawn to scale rather than stylised.
 */
export const BORE_RADIUS = 0.0252;

/**
 * Outer edge of the metal collar bushing. MEASURED at 32.46 mm (Ø64.9 mm) off Ryan's
 * outline, with the scale anchored on the KNOWN 50.4 mm bore rather than on traced
 * pixels — the bore is the one dimension we know exactly, so it's the honest ruler.
 *
 * Was 34.5 mm, which was 5.9% too big: that number came from "correcting" a sloppy
 * first bore measurement by a made-up factor instead of anchoring. Anchored properly,
 * the trace is excellent — the plate's outer radius reads 224.3 mm against a true
 * 225.0 (0.3% off), so the drawing was never the problem.
 *
 * ABSOLUTE, not a ratio — Ryan 2026-07-15: "I believe it's constant across all plate
 * sizes". It's a steel insert sized to the bar, so it does not shrink with the plate.
 */
export const COLLAR_RADIUS = 0.03246;

/**
 * The raised HUB BOSS. Ryan 2026-07-15: "The metal hub needs to come out 1/4" on either
 * side of the plate, and it's surrounded by a ring of rubber on either side which also
 * protrudes 1/4", and then it dips back down onto the plate."
 *
 * So metal and rubber share ONE flat top at +1/4", with only a material change between
 * them (no step), and the dip runs from the rubber's outer edge down to the main face.
 *
 * RUBBER_RING_RADIUS comes from RYAN'S RATIO, not the trace: "the rubber ring is about
 * 1.63x wider than the metal hub ring". The metal ring is 25.2→32.46 = 7.26 mm wide, so
 * the rubber is 11.83 mm, ending at 44.29 mm. (The trace put its edge at ~42.8, a ratio
 * of 1.42 — close, but his eye on the real product wins over ink on a drawing.)
 *
 * The drop is VERTICAL, with rounded corners top and bottom. Ryan 2026-07-15: "the drop
 * from the raised rubber ring to the lower main surface doesn't have much angle to it at
 * all, it's pretty much a vertical drop-down, but the angles between flat and vertical
 * all have a little rounding to the corners."
 *
 * "Not much angle" means VERTICAL, not shallow — an earlier read of it as a ~12° ramp
 * had this exactly backwards and looked worse than the 45° before it. The drop's radial
 * run is therefore not a free parameter: it's just the two corner radii, so the dip
 * lands at RUBBER_RING_RADIUS + 2 x HUB_CORNER_RADIUS ≈ 48.3 mm. That AGREES with the
 * outline, whose one drawn band beyond the collar ends at ~49.0 mm — the drawing said
 * "cliff" all along; the misread was of Ryan's words, not his drawing.
 *
 * All ABSOLUTE, for the same reason as the collar.
 */
export const HUB_BOSS_HEIGHT = 0.00635; // 1/4" proud of the main face, each side
export const RUBBER_RING_RADIUS = 0.04429; // = 32.46 + 1.63 x 7.26 (Ryan's ratio)
export const HUB_CORNER_RADIUS = 0.002; // the "little rounding" at both ends of the drop

/**
 * Round-over where the flat metal hub rolls into the bore. Ryan 2026-07-15: "can you
 * round the hub donut hole edges? We did this on the bumper plate.. I'm pretty sure
 * these grip plates are 2-3x more rounded though, pretty soft curvy hubs."
 *
 * 2.5x `bumper-plate.js`'s `boreFillet` (0.001 — "subtle round-over where the flat hub
 * meets the bore"). It is scaled from THAT and not from the same file's `flangeWrap`
 * (0.00635, the economy plate's ¼" bore flange): 2-3x a ¼" wrap would be 12-19 mm, wider
 * than this plate's entire 7.26 mm metal ring, so the fillet is the only reading that
 * can be meant.
 *
 * Then +75% on top of that (Ryan 2026-07-15, on seeing the 2.5): 2.5 -> 4.375 mm, which
 * eats most of the ring's 7.26 mm and leaves only ~2.9 mm flat — i.e. the hub is now
 * mostly round-over, which is what "pretty soft curvy" asks for.
 *
 * NOTE the clamp in buildGripPlateMesh had to be relaxed for this: it capped the fillet
 * at HALF the ring width (3.63 mm), which would have silently held 4.375 back to 3.63 —
 * delivering ~45% more instead of the 75% asked for, and looking like the number just
 * didn't do much. Now capped at 80% of the ring, so the flat can't vanish entirely.
 */
export const HUB_BORE_FILLET = 0.004375;

/**
 * How far the metal hub sits BELOW the rubber ring where they meet. Ryan 2026-07-15:
 * "we need to have the hubs where they meet the rubber ring be recessed in about 0.5mm
 * on each side… the rubber is just a tiny bit higher up than the metal hubs."
 *
 * So the two do NOT share a plane after all — an earlier note here said they did. Same
 * value and same idea as the dumbbell's `innerRingRecess` (0.0005, "how far the ring is
 * sunk below the rubber inner face"), which is a good sign it's a house detail rather
 * than a one-off.
 */
export const HUB_METAL_RECESS = 0.0005;

/**
 * Ryan's dimension table (2026-07-15): lb → [outer DIAMETER, THICKNESS] in inches.
 *
 * 2.5 and 5 CHARTED 2026-07-31, off Ryan's `2.5, 5, and 10.PNG`. They were held back on
 * 2026-07-15 — "the 5lb and 2.5 lb. plates are a completely different shape, so let's just
 * do the 45, 35, 25, and 10 for now" — and the shape is indeed different: they have NO HAND
 * CUTOUTS. See SMALL_GRIP_* below for what that costs and how it is modelled.
 *
 * ⚠ THE SIZES WERE NEVER THE MISSING PART. Both diameters and thicknesses have been in
 * Ryan's table since 2026-07-15; what was missing was the FACE. Charting them here without
 * the cutout-free branch would render a 2.5 with three hand holes scaled down onto a face
 * that has 5.6 mm of clear material between the hub and where they'd start.
 */
export const GRIP_PLATE_DIMS_LB = Object.freeze({
  2.5: [7.09, 0.94],
  5: [8.66, 1.02],
  10: [10.63, 1.26],
  25: [12.99, 1.65],
  35: [15.35, 1.65],
  45: [17.72, 1.65], // = Ryan's traced outline
});

/**
 * Denominations at or below this are the CUTOUT-FREE plates: a plain annular face, no hand
 * holes, and a hub that eats a far bigger share of the radius.
 *
 * WHY THERE IS A CLIFF HERE rather than a smooth family. The hub is ABSOLUTE (bore, collar
 * and boss are the same physical parts on every plate) while the cutouts are RATIOS of R, so
 * the two scale against each other. Clear material between the hub dip and where the cutouts
 * would begin: 86.5 mm at 45 lb, 32.6 mm at 10 lb, 17.6 mm at 5 lb, 5.6 mm at 2.5 lb. The
 * real product stops cutting holes before that reaches zero, and so do we.
 */
export const SMALL_GRIP_MAX_LB = 5;

/**
 * The cutout-free plates' two art boundaries. Measured 2026-07-31 off Ryan's drawing, with
 * the 10 lb in the same image as the control (it reproduced the known model — bore 0.181R vs
 * 0.187 true, collar 0.231R vs 0.240, rubber ring 0.334R vs 0.328).
 *
 * ⛔ THESE ARE ABSOLUTE METRES. DO NOT "TIDY" THEM INTO FRACTIONS OF R.
 *
 * That is not a style preference, it is the measurement. The 2.5 and the 5 were measured
 * independently, with independently derived pixel scales, and landed on the SAME PHYSICAL
 * NUMBERS: inner ring 38.65 vs 38.92 mm (0.7% apart), outer band 10.96 vs 11.51 mm. As
 * ratios of R those same two facts read as 0.431R vs 0.353R and 0.122R vs 0.102R — i.e.
 * nothing alike, and a ratio fitted to one plate is wrong on the other by a tenth of its
 * radius. Same reasoning as GRIP_SAFE_INSET's own note two constants down.
 */
export const SMALL_GRIP_HUB_RADIUS = 0.0388; // 38.8 mm — art starts outside the hub ring
export const SMALL_GRIP_RIM_BAND = 0.0112; // 11.2 mm — the no-art band in from the rim

/**
 * Artwork keep-clear for the cutout-free plates, REPLACING the 3/8" GRIP_SAFE_INSET.
 *
 * Ryan 2026-07-31, asked whether 3/8" still applies on top of the two no-art rings:
 * *"shrink it for these two."*
 *
 * 3/8" was always a VISUAL margin sized on the 45's 161 mm band (it costs 11.8% of it),
 * never a print-registration one. Applied at both edges of the 2.5's 40 mm band it costs
 * 19 mm — 48% of the entire printable face. 1/8" holds the same proportional bite (~7% of
 * the 2.5's band, ~5% of the 5's, against the 45's 11.8%) and is still several times any
 * pad-print registration tolerance.
 *
 * ONE value for both plates rather than two: a per-plate proportional inset works out at
 * 2.4 / 3.5 mm, which 1/8" (3.175) sits between, so the second constant would buy under a
 * millimetre and add a thing to keep in sync.
 */
/* ⬇ 1/8″ → 1/16″, 2026-08-02 (Ryan: *"I think we could allow the numbers/logos to get a little
 * closer to the edge of the customizable surface to maximize the potential size of these
 * numbers/logos on these already small plates, otherwise they may be too hard to read."*)
 *
 * This is OUR safety margin inside the surface that can physically take art — it is not a
 * manufacturing limit — and on these two it is the single largest thing standing between the mark
 * and the edge: at 1/8″ the two insets together spent **0.250″ of the 2.5 lb's 1.577″ printable
 * band, 16% of the face, on margin.** Halving it returns 1/8″ to the band, +9.4% of box on the
 * 2.5 and +5.9% on the 5.
 *
 * ⛔ TRIED AT 1/16″ AND PUT BACK, 2026-08-02, ON RYAN LOOKING AT IT. Halving it did return 1/8″ to
 * the band and the marks did get bigger — but *"the number is a LITTLE too big, in that it overlaps
 * the customizable area on both sides slightly"* on the 2.5. Measured afterwards, every style on
 * both plates was still mathematically INSIDE the annulus, by 2–7 units out of 225: three
 * hundredths of an inch. That is the finding worth keeping — **"inside the geometry" and "reads as
 * inside" are not the same test, and this margin is what buys the difference.** The raised ring has
 * real width on the rendered face, so ink that stops a hair short of the ring's centre line looks
 * like it is touching it.
 *   The size those two rungs needed came from the other three levers instead (the 65/80 re-peg they
 * should never have worn, the slot padding that was duplicating this inset radially, and the
 * tangential room nobody had claimed) — all of which spend room INSIDE the margin rather than
 * spending the margin. Net of the revert they are still far bigger than they were. */
export const SMALL_GRIP_SAFE_INSET = 0.003175; // 1/8"

/** True for the cutout-free plates. The ONE predicate — every branch in this file, in
 *  render-engine.js and in the layout gating asks this and nothing else. */
export function isSmallGrip(weight) {
  return Number(weight) <= SMALL_GRIP_MAX_LB;
}

/**
 * SAMPLED BY CURVATURE off the raw traced perimeter: 0.25° from 15° out (through the
 * whole sweep and the tips), 2.5° across the flat middle.
 *
 * An earlier table used 1° through the sweep, and that WAS the faceting Ryan saw on the
 * inside of the handles (2026-07-15). Not a mesh-resolution problem: inner(θ) runs
 * 0.599R→0.817R across ~8°, so dr/dθ more than doubles between offsets 18 and 20 — three
 * 1° samples there ARE a 21° corner, and interpolating them more finely just draws the
 * same corner more precisely. Sampling the mesh harder made it WORSE, which is what
 * proved the corner was in the DATA. Re-sampled from the raw perimeter: 21.4° → 9.9°.
 *
 * The hand cutout, traced off Ryan's outline: [degrees from the cutout's centre,
 * inner radius / R, outer radius / R].
 *
 * All THREE cutouts were traced and averaged — they agreed to within 0.005R, so the
 * averaging removes trace noise rather than inventing a shape. (The EZ bend taught
 * that lesson the hard way: a single-sample notch in one trace rendered as a hard V.)
 * Then symmetrised about 0.
 *
 * The shape is a "smile", not a rounded rectangle: the OUTER edge is a dead-flat arc
 * at 0.8515R all the way out, and the ends close by the INNER edge sweeping up from
 * 0.599R to meet it. Hence inner == outer at ±26°.
 *
 * THAT CLOSURE IS LOad-BEARING for the mesh, not just cosmetic: because the band has
 * zero width outside its span, the face stays ONE regular polar grid (the band simply
 * collapses), and the rounded ends fall out of the wall tapering to nothing. No
 * special-cased end caps, no grid alignment constraint, no T-junctions.
 */
export const GRIP_CUTOUT = Object.freeze([
  [-26.238, 0.8167, 0.8168], // tip
  [-26.0, 0.7964, 0.8305],
  [-25.75, 0.786, 0.8365],
  [-25.5, 0.7765, 0.8406],
  [-25.25, 0.7671, 0.8437],
  [-25.0, 0.758, 0.846],
  [-24.75, 0.7491, 0.8478],
  [-24.5, 0.7405, 0.8492],
  [-24.25, 0.732, 0.8501],
  [-24.0, 0.7238, 0.8508],
  [-23.75, 0.7159, 0.8513],
  [-23.5, 0.7082, 0.8516],
  [-23.25, 0.7006, 0.8518],
  [-23.0, 0.6931, 0.852],
  [-22.75, 0.6856, 0.852],
  [-22.5, 0.6786, 0.852],
  [-22.25, 0.672, 0.852],
  [-22.0, 0.6654, 0.8519],
  [-21.75, 0.6585, 0.8519],
  [-21.5, 0.6517, 0.8519],
  [-21.25, 0.645, 0.8519],
  [-21.0, 0.6387, 0.8519],
  [-20.75, 0.6327, 0.8519],
  [-20.5, 0.6268, 0.8519],
  [-20.25, 0.6214, 0.8519],
  [-20.0, 0.6168, 0.852],
  [-19.75, 0.6132, 0.852],
  [-19.5, 0.6104, 0.852],
  [-19.25, 0.608, 0.852],
  [-19.0, 0.6062, 0.852],
  [-18.75, 0.6047, 0.852],
  [-18.5, 0.6035, 0.8519],
  [-18.25, 0.6025, 0.8519],
  [-18.0, 0.6017, 0.8518],
  [-17.75, 0.601, 0.8517],
  [-17.5, 0.6005, 0.8516],
  [-17.25, 0.6, 0.8515],
  [-17.0, 0.5997, 0.8515],
  [-16.75, 0.5994, 0.8514],
  [-16.5, 0.5992, 0.8514],
  [-16.25, 0.5991, 0.8514],
  [-16.0, 0.5989, 0.8514],
  [-15.75, 0.5988, 0.8514],
  [-15.5, 0.5988, 0.8515],
  [-15.25, 0.5987, 0.8515],
  [-15.0, 0.5987, 0.8516],
  [-12.5, 0.5991, 0.8511],
  [-10.0, 0.5994, 0.8514],
  [-7.5, 0.6005, 0.8507],
  [-5.0, 0.6004, 0.8496],
  [-2.5, 0.6004, 0.8501],
  [0.0, 0.6021, 0.8497],
  [2.5, 0.6004, 0.8501],
  [5.0, 0.6004, 0.8496],
  [7.5, 0.6005, 0.8507],
  [10.0, 0.5994, 0.8514],
  [12.5, 0.5991, 0.8511],
  [15.0, 0.5987, 0.8516],
  [15.25, 0.5987, 0.8515],
  [15.5, 0.5988, 0.8515],
  [15.75, 0.5988, 0.8514],
  [16.0, 0.5989, 0.8514],
  [16.25, 0.5991, 0.8514],
  [16.5, 0.5992, 0.8514],
  [16.75, 0.5994, 0.8514],
  [17.0, 0.5997, 0.8515],
  [17.25, 0.6, 0.8515],
  [17.5, 0.6005, 0.8516],
  [17.75, 0.601, 0.8517],
  [18.0, 0.6017, 0.8518],
  [18.25, 0.6025, 0.8519],
  [18.5, 0.6035, 0.8519],
  [18.75, 0.6047, 0.852],
  [19.0, 0.6062, 0.852],
  [19.25, 0.608, 0.852],
  [19.5, 0.6104, 0.852],
  [19.75, 0.6132, 0.852],
  [20.0, 0.6168, 0.852],
  [20.25, 0.6214, 0.8519],
  [20.5, 0.6268, 0.8519],
  [20.75, 0.6327, 0.8519],
  [21.0, 0.6387, 0.8519],
  [21.25, 0.645, 0.8519],
  [21.5, 0.6517, 0.8519],
  [21.75, 0.6585, 0.8519],
  [22.0, 0.6654, 0.8519],
  [22.25, 0.672, 0.852],
  [22.5, 0.6786, 0.852],
  [22.75, 0.6856, 0.852],
  [23.0, 0.6931, 0.852],
  [23.25, 0.7006, 0.8518],
  [23.5, 0.7082, 0.8516],
  [23.75, 0.7159, 0.8513],
  [24.0, 0.7238, 0.8508],
  [24.25, 0.732, 0.8501],
  [24.5, 0.7405, 0.8492],
  [24.75, 0.7491, 0.8478],
  [25.0, 0.758, 0.846],
  [25.25, 0.7671, 0.8437],
  [25.5, 0.7765, 0.8406],
  [25.75, 0.786, 0.8365],
  [26.0, 0.7964, 0.8305],
  [26.238, 0.8167, 0.8168], // tip
]);

/**
 * Angular centres of the three cutouts, in the plate's UPRIGHT orientation: a cutout at
 * the BOTTOM (270°), two up top, and therefore a raised arc dead at 12 o'clock.
 *
 * Traced at 89.5/209.5/329.5 and exactly 120° apart. Those were carried straight into
 * 3D at first, which came out 60° off (Ryan 2026-07-15) — because IMAGE space has +Y
 * DOWN while the mesh has +Y UP, so the trace's "89.5° = 6 o'clock" silently became
 * 12 o'clock. Rotating 60° lands it where his drawing actually sits. (±60 give the same
 * set, since the plate has 120° symmetry.)
 *
 * The raised arcs follow automatically — they're defined as the gaps between cutouts, so
 * they rotate with these and need no change of their own.
 */
export const CUTOUT_CENTRES_DEG = Object.freeze([30, 150, 270]);

/**
 * Keep-clear margin for artwork: 3/8" back from the rim and from every cutout edge.
 *
 * Ryan 2026-07-15: "the logo-able area can go pretty far out towards the edge on the
 * outer rim between the handles too. I don't know the exact extent... but safely if we
 * pull back 3/8" on the rim and around the handle cutouts, that should be fine." So this
 * is a deliberately CONSERVATIVE stand-in for a real print spec, not a measured limit —
 * if the true extent ever arrives, change this one number.
 *
 * ABSOLUTE, never a ratio. Like the bore, a print margin is a physical distance, so it
 * eats a bigger fraction of a small plate (0.042R at 45 lb -> 0.071R at 10 lb). Anything
 * that treats it as a constant fraction of R will over-print the 10 and waste the 45.
 */
export const GRIP_SAFE_INSET = 0.009525; // 3/8"

/**
 * How much of the 3/8" margin survives at a cutout's TIPS, as a fraction.
 *
 * 1.0 = OFF — the margin is uniform all the way round, which is where this stands.
 *
 * It was briefly 0.5, on the theory that a constant offset bulges around a convex corner
 * (at a tip it becomes a disc of the full radius, so it reads fatter than the same offset
 * along a straight edge) and the tips therefore needed a smaller number to LOOK even.
 * Ryan tried it and said it made things worse, so the theory is unproven and the taper is
 * off pending a clearer read of what he's seeing.
 *
 * The mechanism stays because the per-point inset it introduced is worth keeping either
 * way — but do NOT turn this down again without evidence.
 */
export const GRIP_TIP_INSET_FACTOR = 1.0;

/**
 * Inner edge of the plate's OUTERMOST RING, as a ratio of R. Artwork never enters it.
 *
 * Ryan 2026-07-15: "on the outer most ring of the plate, no branding there." The ring
 * is a real feature drawn on his outline — a hairline circle whose stroke spans
 * r = 0.885..0.918R, centred at ~0.902R (there's a matching one around the hub). It is
 * NOT anti-aliasing; an earlier pass here checked with too blunt a threshold, found
 * nothing, and wrongly concluded the plate was a plain flat disc.
 *
 * A RATIO, unlike the bore and the safe inset — because it's a proportional design
 * feature of the casting, not a physical margin. Worth being explicit about, since the
 * neighbouring constants are absolute for exactly the opposite reason.
 */
export const GRIP_OUTER_RING_RATIO = 0.906;

/**
 * The RAISED RIM ZONES — three arcs standing 1/4" proud on each face, in the gaps
 * BETWEEN the cutouts (Ryan 2026-07-15, `Raised-Zones.webp`).
 *
 * NOT A CONTINUOUS RING — which corrects an earlier reading here. Measured off his
 * highlight: 3 arcs, each exactly 67.0°, centred 29.5/149.5/269.5, radial band
 * 0.906→1.00R. The cutout centres in that image (89.5/209.5/329.5) are identical to the
 * outline's, confirming both drawings are the same plate.
 *
 * THEIR ANGULAR EXTENT IS DERIVED, NOT STORED. The cutouts span ±26.238°, so a cutout
 * ending at 116.24° is followed by an arc starting at 116.0° — the arcs simply fill the
 * gaps (120 − 2×26.238 = 67.52° wide; measured 67.0). So a zone is "the outer band
 * wherever the cutout band is CLOSED", which needs no new angular constants and follows
 * the cutout table automatically if it ever changes.
 *
 * The inner edge doubles as GRIP_OUTER_RING_RATIO: a hairline traced independently off
 * the OTHER drawing put it at ~0.902, and the yellow starts at 0.906 — two sources, one
 * edge.
 *
 * They rise the same way the hub does: vertical, with rounded corners at both ends
 * (Ryan: "raised about ¼" on each side of the plate, with smooth corners and rounding").
 */
export const RAISE_ZONE_HEIGHT = 0.00635; // 1/4", same as the hub boss

/**
 * The brandable-zone descriptor (V2 plan §3): what artwork may occupy, as distinct
 * from what the plate physically IS.
 *
 * The headline: the zone is ONE CONNECTED REGION, not two rings. The centre field and
 * the rim field join through the three ~67° solid arcs between the cutouts, so a design
 * can flow across the whole face and simply avoid the holes. Both `clean-center` and
 * `clean-rim` are supported (see the note at the top of this file on why the plan's
 * fear of a hub denying `clean-center` doesn't materialise here).
 *
 * The cutout keep-out is the traced shape DILATED by the inset — radially on both
 * edges, and angularly by the inset's arc-equivalent at each radius (so the margin is
 * a true distance, not a constant angle, which would be too tight near the bore and
 * too loose at the rim).
 *
 * @param {Partial<typeof DEFAULT_GRIP_PLATE_OPTS>} [opts]
 */
export function gripBrandZone(opts = {}) {
  const params = { ...DEFAULT_GRIP_PLATE_OPTS, ...opts };
  if (opts.weight != null && opts.outerRadius == null) {
    const [r, t] = gripPlateDims(opts.weight);
    params.outerRadius = r;
    params.thickness = t;
  }
  const R = params.outerRadius;

  /* ── THE CUTOUT-FREE PLATES GET A PLAIN ANNULUS, AND RETURN EARLY ──────────────────────
   *
   * Ryan 2026-07-31: *"no handles on the 2.5 or 5.. there's an outer edge which doesn't get
   * art, and inner ring which doesn't get art, then the space inbetween gets the art. a lot
   * like the training bumper in that regard."*
   *
   * A SEPARATE RETURN, not conditionals threaded through the machinery below. Everything
   * from here down exists to dodge three hand holes — the per-point `outline`, the tip
   * inset taper, the distance test, the denial of 'clean-rim'. On a face with no holes each
   * of those is not "disabled", it is MEANINGLESS, and a flag that makes a meaningless
   * computation return early is how the next reader ends up believing the cutout code still
   * governs this plate.
   *
   * ⚠ THE INNER LIMIT KEYS OFF THE HUB RING, NOT THE COLLAR. Below, the inner limit is
   * `collarRadius + inset` = 32.46 mm + margin. Here the measured no-art ring is at 38.8 mm
   * — over 6 mm further out. Using the collar would start artwork INSIDE the boundary Ryan
   * specified, and on a 2.5 lb that is a fifth of the whole printable band sitting on the
   * hub boss.
   *
   * Both limits are ABSOLUTE distances (see SMALL_GRIP_HUB_RADIUS / SMALL_GRIP_RIM_BAND),
   * so unlike the 45's they are NOT computed from a ratio of R. */
  if (isSmallGrip(params.weight)) {
    /* ⚠ `opts.safeInset`, NOT `params.safeInset`. `params` is spread over
     * DEFAULT_GRIP_PLATE_OPTS, which always carries `safeInset: GRIP_SAFE_INSET` — so
     * `params.safeInset != null` is true on EVERY call and this branch silently took the
     * 45's 3/8". It cost 48% of the 2.5's band and looked exactly like the constant not
     * working. Only an explicit caller override may win here. */
    const smInset = opts.safeInset != null ? opts.safeInset : SMALL_GRIP_SAFE_INSET;
    const ringR = R - SMALL_GRIP_RIM_BAND;
    const inner = SMALL_GRIP_HUB_RADIUS + smInset;
    const outer = ringR - smInset;
    return {
      outerSafeRadius: outer,
      innerSafeRadius: inner,
      inset: smInset,
      ringRadius: ringR,
      outerSafeRatio: outer / R,
      innerSafeRatio: inner / R,
      /* 'clean-rim' is GRANTED here, where the cutout plates deny it. Their denial is not
       * about the rim being precious — it is that their raised zone is three ARCS with gaps
       * between, so no band runs continuously round for arc text to follow. This face has an
       * uninterrupted rim band, so a continuous ring genuinely exists.
       *   Ryan has ruled out text on these two plates, so nothing consumes it today. It is
       * still the honest answer to "what shape is this face", and a zone that lies about
       * that is a trap for whoever enables text later. */
      zoneClasses: ['full-face', 'clean-center', 'clean-rim'],
      outline: [], // no holes — nothing to keep clear of
      /** True where artwork may print. With no cutouts this is just the two radii. */
      allows(r) {
        return r >= inner && r <= outer;
      },
    };
  }

  const inset = params.safeInset != null ? params.safeInset : GRIP_SAFE_INSET;
  // Artwork stops 3/8" short of the OUTER RING's inner edge — not 3/8" short of the
  // plate's rim. Ryan gave both instructions and the ring is the binding one: it puts
  // the limit at 0.860R on a 45, where measuring from the rim would have allowed
  // 0.958R and printed straight across a ring he said to keep clear.
  const ringR = (params.outerRingRatio != null ? params.outerRingRatio : GRIP_OUTER_RING_RATIO) * R;
  const outerSafeRadius = ringR - inset;
  const half = CUTOUT_HALF_DEG;
  /**
   * How far out artwork may run. UNIFORM — the same limit at every angle.
   *
   * A pass in between made this sector-aware, letting art run to the plate's rim inside
   * each cutout's angular span on the reasoning that the rim is flat there (the raised
   * arcs only occupy the gaps). Ryan 2026-07-15: "you also added it to the outer edges
   * just outside the handles (3 pieces). remove these." His original rule stands as
   * given — "on the outer most ring of the plate, no branding there" — and it means the
   * whole ring, not just the raised parts of it. Being clever about the geometry was not
   * the same as listening.
   */
  const outerSafeAt = () => outerSafeRadius;
  return {
    outerSafeRadius,
    innerSafeRadius: params.collarRadius + inset, // and clears the collar
    inset,
    ringRadius: ringR,
    outerSafeRatio: outerSafeRadius / R,
    innerSafeRatio: (params.collarRadius + inset) / R,
    // 'clean-rim' is DENIED — but for a subtler reason than first recorded here. The
    // rim is NOT off-limits everywhere: the raised zone is three ARCS in the gaps
    // between cutouts, so inside a cutout's own angular span the rim is flat and
    // printable. What's denied is a rim band CONTINUOUS all the way round, which is what
    // arc text needs. So: full-face and clean-center yes, clean-rim no — and templates
    // wanting rim text are filtered out, while a full-face design still gets to use the
    // rim in three of six sectors.
    zoneClasses: ['full-face', 'clean-center'],
    /**
     * Every point on ONE cutout's outline, in the cutout's own frame (axis on +x), as
     * [x, y] in metres. Built once per zone.
     *
     * The margin is measured as a DISTANCE to this outline. It used to be a radial band
     * test — `r` inside [ri·R − inset, ro·R + inset] — which is right along the cutout's
     * sides and WRONG at its ends: beyond ±26.238° the band closes to zero width and a
     * `ro - ri < 1e-4` guard skipped the keep-out altogether, so paint ran flush to the
     * tip while the sides kept their full 3/8" (Ryan 2026-07-15, the red circle). A true
     * distance has no such special case — the ends are just more outline.
     */
    outline: (() => {
      const pts = [];
      // Each point carries its OWN inset, tapering toward the tips.
      //
      // A constant offset does not LOOK constant around a convex corner: along the
      // hole's sides it is a thin strip, but at a tip it becomes a disc of that radius
      // and bulges — reading much fatter even though it measures the same. That is
      // Ryan's "hugs the hand hole too closely... not consistent with how it hugs the
      // back-side of the hole" (2026-07-15): the back-side is a long gentle edge, the
      // tip is a corner. So the tips take a smaller number to look the same.
      const tipF = params.tipInsetFactor != null ? params.tipInsetFactor : GRIP_TIP_INSET_FACTOR;
      const easeStart = CUTOUT_HALF_DEG * 0.7; // full inset inboard of here
      for (const row of GRIP_CUTOUT) {
        const t = Math.min(1, Math.max(0, (Math.abs(row[0]) - easeStart) / (CUTOUT_HALF_DEG - easeStart)));
        const f = 1 + (tipF - 1) * t; // 1 along the sides -> tipF at the tip
        const a = (row[0] * Math.PI) / 180;
        pts.push([row[1] * R * Math.cos(a), row[1] * R * Math.sin(a), inset * f]);
      }
      for (let i = GRIP_CUTOUT.length - 1; i >= 0; i--) {
        const row = GRIP_CUTOUT[i];
        const t = Math.min(1, Math.max(0, (Math.abs(row[0]) - easeStart) / (CUTOUT_HALF_DEG - easeStart)));
        const f = 1 + (tipF - 1) * t;
        const a = (row[0] * Math.PI) / 180;
        pts.push([row[2] * R * Math.cos(a), row[2] * R * Math.sin(a), inset * f]);
      }
      return pts;
    })(),
    /**
     * True where artwork may print: inside the rim/arc limit, clear of the collar, and
     * at least `inset` from every cutout's outline.
     * @param {number} r radius in metres
     * @param {number} thetaDeg
     */
    allows(r, thetaDeg) {
      if (r > outerSafeAt(thetaDeg) || r < params.collarRadius + inset) return false;
      const th = (thetaDeg * Math.PI) / 180;
      const px = r * Math.cos(th);
      const py = r * Math.sin(th);
      for (const c of CUTOUT_CENTRES_DEG) {
        const d = ((thetaDeg - c + 540) % 360) - 180;
        if (Math.abs(d) > 60) continue; // too far to matter; cutouts are 120 apart
        // inside the hole itself?
        if (Math.abs(d) < half) {
          const [ri, ro] = cutoutAt(d);
          if (ro - ri > 1e-4 && r > ri * R && r < ro * R) return false;
        }
        // within `inset` of its outline? (rotate the point into the cutout's frame)
        const ca = (c * Math.PI) / 180;
        const cs = Math.cos(-ca), sn = Math.sin(-ca);
        const qx = px * cs - py * sn;
        const qy = px * sn + py * cs;
        for (const [ox, oy, oi] of this.outline) {
          const dx = qx - ox, dy = qy - oy;
          if (dx * dx + dy * dy < oi * oi) return false; // per-point inset — see `outline`
        }
      }
      return true;
    },
  };
}

/** Half-span of the cutout in degrees (the table's outermost sample). */
const CUTOUT_HALF_DEG = 26.238;

export const DEFAULT_GRIP_PLATE_OPTS = Object.freeze({
  weight: 45, // lb; drives outerRadius + thickness via gripPlateDims
  outerRadius: (17.72 * IN) / 2, // 45 lb
  thickness: 1.65 * IN,
  boreRadius: BORE_RADIUS,
  collarRadius: COLLAR_RADIUS,
  hubBossHeight: HUB_BOSS_HEIGHT, // metal + rubber stand this proud of each face
  rubberRingRadius: RUBBER_RING_RADIUS,
  hubCornerRadius: HUB_CORNER_RADIUS,
  hubBoreFillet: HUB_BORE_FILLET, // round-over into the bore — 2.5x the bumper's
  hubMetalRecess: HUB_METAL_RECESS, // metal sits this far below the rubber
  raiseZoneHeight: RAISE_ZONE_HEIGHT, // the 3 rim arcs stand this proud
  outerRingRatio: GRIP_OUTER_RING_RATIO, // their inner edge
  safeInset: GRIP_SAFE_INSET, // artwork keep-clear; see gripBrandZone
  tipInsetFactor: GRIP_TIP_INSET_FACTOR, // less margin at the cutout tips, so they LOOK even
  // The OUTER RIM's round-over and the CUTOUT LIPS' are separate numbers on purpose.
  // They were one shared `edgeRadius`, but Ryan signed off the cutout rounding ("the
  // rounding you added to the corners looks good") and then asked for the outer corner
  // to be "a little more roundy" — one value can't do both without undoing an approval.
  rimEdgeRadius: 0.006, // outer rim: 6.0mm, up from 4.5 (Ryan 2026-07-15, "just a little")
  edgeRadius: 0.0045, // cutout lips — APPROVED, leave alone
  edgeSegments: 8,
  // BASE ring only — the cutout tips add their own columns on top (see buildGripPlateMesh),
  // so this just has to keep the rim circle smooth, not resolve the cutouts.
  radialSegments: 180,
  // Radial rings per clean field, per face. 2, not 10: the faces are FLAT and the brand
  // UV is planar (affine in x,y), so interpolation across one big quad is EXACT — extra
  // rings add triangles and change nothing. 10 was a reflex carried over from the curved
  // products, and cost ~30k tris for no visible difference.
  faceRings: 2,
});

/** [outerRadius, thickness] in metres for a charted weight; nearest chart row if
 *  the exact weight isn't listed. lb only — grip plates are spec'd in lb. */
export function gripPlateDims(weight) {
  const table = GRIP_PLATE_DIMS_LB;
  let entry = table[weight];
  if (entry == null) {
    const keys = Object.keys(table).map(Number);
    const nearest = keys.reduce((a, b) => (Math.abs(b - weight) < Math.abs(a - weight) ? b : a), keys[0]);
    entry = table[nearest];
  }
  return [(entry[0] * IN) / 2, entry[1] * IN];
}

/** Material slot indices — mirror the dumbbell/plate so reveal-3d's rig just works. */
export const MATERIAL_BRAND_FACE = 0;
export const MATERIAL_BODY = 1;
export const MATERIAL_METAL = 2;

/**
 * Reads the cutout table at an arbitrary angle offset (degrees from a cutout's
 * centre), returning [innerR, outerR] as ratios of R. Outside the span both collapse
 * to the closure radius, so the band has zero width and the face grid stays regular.
 */
function cutoutAt(offsetDeg) {
  const T = GRIP_CUTOUT;
  const closed = T[0][1];
  if (Math.abs(offsetDeg) >= CUTOUT_HALF_DEG) return [closed, closed];
  // The table is NON-UNIFORM (0.25° through the tips, 2° across the flat middle), so
  // the sample index cannot be computed from the angle — it must be searched. An
  // earlier version divided by a constant step and silently read the wrong rows.
  let i = 0;
  while (i < T.length - 2 && T[i + 1][0] < offsetDeg) i++;
  const [t0, i0, o0] = T[i];
  const [t1, i1, o1] = T[i + 1];
  const f = t1 === t0 ? 0 : (offsetDeg - t0) / (t1 - t0);
  return [i0 + (i1 - i0) * f, o0 + (o1 - o0) * f];
}

/**
 * The cutout band at an absolute angle θ (degrees): the [inner, outer] ratios of
 * whichever cutout θ falls in, or a collapsed band if it falls in solid material.
 */
function bandAt(thetaDeg, centres = CUTOUT_CENTRES_DEG) {
  for (const c of centres) {
    const d = ((thetaDeg - c + 540) % 360) - 180; // signed offset, -180..180
    if (Math.abs(d) < CUTOUT_HALF_DEG) return cutoutAt(d);
  }
  const closed = GRIP_CUTOUT[0][1];
  return [closed, closed];
}

/**
 * Pure mesh builder (node-testable, no THREE).
 * @param {Partial<typeof DEFAULT_GRIP_PLATE_OPTS>} [opts]
 */
export function buildGripPlateMesh(opts = {}) {
  const params = { ...DEFAULT_GRIP_PLATE_OPTS, ...opts };
  if (opts.weight != null && opts.outerRadius == null && opts.thickness == null) {
    const [r, t] = gripPlateDims(opts.weight);
    params.outerRadius = r;
    params.thickness = t;
  }
  const R = params.outerRadius;

  /* ── CUTOUT-FREE PLATES CONFIGURE THEMSELVES FROM THE WEIGHT ──────────────────────────
   *
   * Derived HERE rather than at the call sites on purpose. `buildGripPlateMesh` is reached
   * from reveal-3d, the previews, the packet poses and the batch renderer; a caller that
   * forgot to pass `cutoutCentres: []` would render a 2.5 lb plate with three hand holes
   * scaled onto a face that has 5.6 mm of clear material, and nothing would warn. Deriving
   * it from the weight the caller already passes makes that unforgettable.
   *
   * An explicit opt still wins, so a preview can force either shape.
   */
  const small = isSmallGrip(params.weight);
  // No hand holes. `bandAt` returns a COLLAPSED band for every angle when the centres list
  // is empty, and the face grid is built to absorb exactly that (see GRIP_CUTOUT's note:
  // "because the band has zero width outside its span, the face stays ONE regular polar
  // grid"). So the cutout-free face needs no new code path — the zero-width ring's quads
  // are dropped by the same per-triangle degeneracy guard the cutout TIPS already rely on.
  const cutCentres = params.cutoutCentres != null ? params.cutoutCentres : (small ? [] : CUTOUT_CENTRES_DEG);
  // The hub boss is SMALLER on these two — 38.8 mm measured, against the 45's 44.29 mm
  // rubber ring. Reusing the 45's number costs 14% of the 2.5's printable face.
  const rubberRing = opts.rubberRingRadius != null
    ? opts.rubberRingRadius
    : (small ? SMALL_GRIP_HUB_RADIUS : params.rubberRingRadius);
  // The raised outer band is an ABSOLUTE 11.2 mm in from the rim here, not 0.906R. At 2.5 lb
  // those differ by 3 mm on a 34 mm band.
  const outerRingRatio = opts.outerRingRatio != null
    ? opts.outerRingRatio
    : (small ? (R - SMALL_GRIP_RIM_BAND) / R : params.outerRingRatio);
  /** The cutout band at an angle, bound to THIS plate's cutout set. */
  const band = (d) => bandAt(d, cutCentres);

  const HT = params.thickness / 2;
  const bore = params.boreRadius;
  const collar = params.collarRadius;
  const seg = Math.max(48, Math.floor(params.radialSegments));
  const rings = Math.max(2, Math.floor(params.faceRings));
  const er = Math.max(0, Math.min(params.edgeRadius, 0.45 * HT, 0.2 * R)); // cutout lips
  const rimEr = Math.max(0, Math.min(params.rimEdgeRadius, 0.45 * HT, 0.2 * R)); // outer rim
  const eSeg = Math.max(1, Math.floor(params.edgeSegments));
  const rFlat = R - rimEr; // where the flat face ends and the rim round begins
  const faceInner = rubberRing + 2 * Math.min(params.hubCornerRadius, params.hubBossHeight / 2.5); // the flat face starts where the hub dip lands

  const positions = [];
  const normals = [];
  const uvs = [];
  const colors = [];
  const tangents = [];
  const idx = { brand: [], body: [], metal: [] };
  let vertCount = 0;

  /** Planar UV over the whole face — the design fills the disc, centred. */
  const planarUV = (x, y) => [x / (2 * R) + 0.5, y / (2 * R) + 0.5];
  const CUV = [0.5, 0.5];

  /** Squared distance between two points — for rejecting collapsed quad corners. */
  const d2 = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;

  /**
   * True if a quad has collapsed — two corners coincident, so one of its two triangles
   * has zero area.
   *
   * Zero-area triangles are NOT harmless. Their vertex normals are still interpolated
   * and their edges still get rasterised at sub-pixel width, so they show up as bright
   * slivers — exactly the four white crescents Ryan saw at the cutout tips (2026-07-15),
   * where the lip radius tapers to nothing and every lip quad collapsed to a point.
   * Rejecting them here rather than at each call site keeps the guard un-forgettable.
   */
  const collapsed = (p) => d2(p[0], p[1]) < 1e-14 || d2(p[1], p[2]) < 1e-14 || d2(p[2], p[3]) < 1e-14 || d2(p[3], p[0]) < 1e-14;

  /**
   * Geometric normal of a quad, taken from whichever of its two triangles is NOT
   * degenerate.
   *
   * This must not just use (0,1,2). At a cutout tip the lip radius tapers to zero and
   * ONE side of the quad collapses — which side depends on whether the column pair is
   * entering or leaving the cutout. Where p0/p1 collapse, (0,1,2) has a ~zero normal,
   * the winding dot-product becomes noise, and the `>= 0` test falls through to the
   * default order — a coin flip. Half of those come out backfaced, get culled by
   * FrontSide, and show background as a white sliver.
   *
   * That is why the tips were ASYMMETRIC — clean at +26.1° (where p2/p3 collapse and
   * (0,1,2) survives), slivered at -26.1° (where p0/p1 collapse and it doesn't). Ryan
   * spotted it as "all fixed on one side, but the other side still has triangles"
   * (2026-07-15), which is what identified this: mirrored geometry behaving differently
   * can only be something order-dependent, not the shape.
   *
   * @returns {[number,number,number]|null} null if the whole quad is degenerate
   */
  function quadNormal(p) {
    for (const [i, j, k] of [[0, 1, 2], [0, 2, 3], [1, 2, 3]]) {
      const ax = p[j][0] - p[i][0], ay = p[j][1] - p[i][1], az = p[j][2] - p[i][2];
      const bx = p[k][0] - p[i][0], by = p[k][1] - p[i][1], bz = p[k][2] - p[i][2];
      const gx = ay * bz - az * by, gy = az * bx - ax * bz, gz = ax * by - ay * bx;
      if (gx * gx + gy * gy + gz * gz > 1e-24) return [gx, gy, gz];
    }
    return null;
  }

  /**
   * Emits a quad as up to TWO triangles, dropping any that are degenerate.
   *
   * This is where zero-area triangles must be rejected — not at the quad level. A lip
   * quad at a cutout tip is collapsed on ONE side (the radius tapers to zero there) but
   * still has real width on the other, so it is a TRIANGLE, not nothing. Skipping the
   * whole quad — which an earlier guard here did — left a hole between the face and the
   * wall, and a hole shows background: the same white sliver, just a different cause
   * (Ryan 2026-07-15, twice). Per-triangle rejection keeps the bridge and drops only the
   * genuinely zero-area half.
   */
  function push(slot, verts) {
    const base = vertCount;
    for (const v of verts) {
      positions.push(v.pos[0], v.pos[1], v.pos[2]);
      normals.push(v.normal[0], v.normal[1], v.normal[2]);
      uvs.push(v.uv[0], v.uv[1]);
      colors.push(1, 1, 1);
      tangents.push(1, 0, 0, 1);
      vertCount++;
    }
    const tri = (a, b, c) => {
      const A = verts[a].pos, B = verts[b].pos, C = verts[c].pos;
      const ax = B[0] - A[0], ay = B[1] - A[1], az = B[2] - A[2];
      const bx = C[0] - A[0], by = C[1] - A[1], bz = C[2] - A[2];
      const gx = ay * bz - az * by, gy = az * bx - ax * bz, gz = ax * by - ay * bx;
      if (gx * gx + gy * gy + gz * gz < 1e-24) return; // zero area — would rasterise as a sliver
      idx[slot].push(base + a, base + b, base + c);
    };
    tri(0, 1, 2);
    tri(0, 2, 3);
  }

  /** Emits a quad with an intended outward normal, auto-reversing if the geometric
   *  normal opposes it — the same winding guard the dumbbell uses. */
  function quad(slot, p, n, uvFn) {
    const g = quadNormal(p); // from a non-degenerate triangle — see quadNormal
    if (!g) return;
    const order = g[0] * n[0] + g[1] * n[1] + g[2] * n[2] >= 0 ? [0, 1, 2, 3] : [3, 2, 1, 0];
    push(
      slot,
      order.map((i) => ({ pos: p[i], normal: n, uv: uvFn ? uvFn(p[i]) : CUV }))
    );
  }

  const rad = (d) => (d * Math.PI) / 180;
  const P = (r, d, z) => [r * Math.cos(rad(d)), r * Math.sin(rad(d)), z];

  // ANGULAR COLUMNS ARE NON-UNIFORM — dense where the shape turns, coarse where it
  // doesn't. A uniform grid cannot render this plate: each cutout tip is a rounded
  // corner spanning ~1°, and a uniform `seg` fine enough to resolve it (>1400 columns)
  // would spend nearly all of them on the flat arcs where nothing happens. So the
  // columns are the union of a coarse base ring and EVERY angle in the cutout table,
  // placed at each of the three cutout centres — the mesh samples exactly where the
  // measurement says detail lives.
  //
  // This is why the TABLE's density is a whole-plate cost: a column spans the full
  // radius, so sampling the cutout edge harder also rings the rim and the face. Keep the
  // table only as dense as its curvature needs (it is sampled by curvature for exactly
  // this reason) and don't add columns here that the shape doesn't ask for — a duplicate
  // set nudged 0.08% off each tip used to live here and cost ~300 columns for nothing.
  const colSet = new Set();
  for (let i = 0; i < seg; i++) colSet.add(Number((((i / seg) * 360).toFixed(6))));
  for (const c of cutCentres) {
    for (const row of GRIP_CUTOUT) {
      colSet.add(Number(((((c + row[0]) % 360) + 360) % 360).toFixed(6)));
    }
  }
  const TH = [...colSet].sort((a, b) => a - b);
  const NC = TH.length;
  /** Column i's angle; column NC wraps to TH[0]+360 so the last quad closes the ring. */
  const th = (i) => (i < NC ? TH[i] : TH[i - NC] + 360);

  /**
   * How proud the rim stands at angle d: the full raise in the gaps BETWEEN cutouts,
   * zero across a cutout's own angular span. The arcs are exactly the gaps, so this
   * reads the cutout table rather than storing its own angles — if the cutouts move,
   * the arcs move with them.
   */
  function raiseAt(d) {
    const [bi, bo] = band(d);
    return bo - bi > 1e-6 ? 0 : params.raiseZoneHeight; // band open => inside a cutout
  }

  /**
   * The rim's RISE profile as (r, height) — the hub dip run backwards: a fillet off the
   * main face, a vertical climb, then a round-over onto the raised arc. Same treatment,
   * so the two read as one language of edge.
   */
  const RISE = (() => {
    const rz = params.raiseZoneHeight;
    const rr = Math.min(params.hubCornerRadius, rz / 2.5);
    const ring = outerRingRatio * R;
    const N = 5;
    const pts = [];
    for (let k = 0; k <= N; k++) {
      const a = (270 + (k / N) * 90) * (Math.PI / 180); // concave fillet off the face
      pts.push([ring - 2 * rr + rr * Math.cos(a), rr + rr * Math.sin(a)]);
    }
    pts.push([ring - rr, rz - rr]); // straight up the face of the step
    for (let k = 0; k <= N; k++) {
      const a = (180 - (k / N) * 90) * (Math.PI / 180); // convex round onto the arc top
      pts.push([ring + rr * Math.cos(a), rz - rr + rr * Math.sin(a)]);
    }
    return pts;
  })();
  const riseStart = RISE[0][0]; // where the rise leaves the main face
  const riseEnd = RISE[RISE.length - 1][0]; // where the raised arc becomes flat
  /** Outward normal at RISE[i], from the local tangent in the (r, height) plane. */
  const riseN = (i, s, d) => {
    const a = RISE[Math.max(0, i - 1)];
    const b = RISE[Math.min(RISE.length - 1, i + 1)];
    const dr = b[0] - a[0];
    const dz = b[1] - a[1];
    const L = Math.hypot(dz, dr) || 1;
    return [(-dz / L) * Math.cos(rad(d)), (-dz / L) * Math.sin(rad(d)), (s * dr) / L];
  };

  /**
   * Fillet radius on the cutout lip at angle d — the hand-hole's equivalent of the
   * plate's rounded rim (Ryan: a hand grips in here, so a right angle "would cut your
   * hand and looks odd").
   *
   * CLAMPED to a share of the band's local width, not a constant. The band narrows to
   * nothing at each tip, so a fixed radius would eat past the far side and invert the
   * geometry there; this tapers the round-over to zero exactly as the hole closes,
   * which is also what a real casting does — a slot narrower than the tool can't carry
   * a full-radius break.
   */
  function cutEdgeR(d) {
    const [bi, bo] = band(d);
    const halfWidth = ((bo - bi) * R) / 2;
    return Math.max(0, Math.min(er, halfWidth * 0.9));
  }

  // ===========================================================================
  // 1. BRAND FACES (±Z) — a polar grid, skipping the cutout band.
  //    Ring positions are per-column and follow cutIn(θ)/cutOut(θ) exactly, so the
  //    face's boundary IS the cutout perimeter — no cracks, no T-junctions.
  // ===========================================================================
  for (const s of [1, -1]) {
    const z = s * HT;
    const n = [0, 0, s];
    // BACK face (s<0): mirror U so the design reads correctly from behind — exactly the
    // bumper's back ring-B trick. Without it the grip's weight/logo read backwards from
    // the reverse side (Ryan 2026-07-16: "the 45 lb is facing the wrong way"). Front (s>0)
    // is unchanged.
    const brandUV = (x, y) => { const u = planarUV(x, y); return s < 0 ? [1 - u[0], u[1]] : u; };
    for (let i = 0; i < NC; i++) {
      const d0 = th(i);
      const d1 = th(i + 1);
      const [in0, out0] = band(d0);
      const [in1, out1] = band(d1);
      const a0 = in0 * R, b0 = out0 * R;
      const a1 = in1 * R, b1 = out1 * R;
      // The flat face stops short of each cutout by the local fillet radius, so the
      // hole's lip can roll over instead of ending in a 90° corner (Ryan 2026-07-15:
      // "these are currently at abrupt right angles, which would cut your hand").
      const e0 = cutEdgeR(d0);
      const e1 = cutEdgeR(d1);
      // centre field: collar -> cutIn, less the fillet
      for (let j = 0; j < rings; j++) {
        const f0 = j / rings, f1 = (j + 1) / rings;
        const A0 = a0 - e0, A1 = a1 - e1;
        quad(
          'brand',
          [
            P(faceInner + (A0 - faceInner) * f0, d0, z),
            P(faceInner + (A0 - faceInner) * f1, d0, z),
            P(faceInner + (A1 - faceInner) * f1, d1, z),
            P(faceInner + (A1 - faceInner) * f0, d1, z),
          ],
          n,
          (p) => brandUV(p[0], p[1])
        );
      }
      // Rim field -> the plate's edge. EVERY column carries the same ring structure and
      // only its HEIGHT changes: 0 across a cutout's span, the RISE profile in the gaps.
      // So the quad spanning a raised column and a flat one IS the arc's end wall — it
      // falls out of the topology. Emitting a separate wall instead means matching its
      // vertices to two different neighbours' rings, which T-junctioned both sides
      // (888 open edges) before this was rewritten.
      //
      // Ryan 2026-07-15: the raised bits sit "flush with the outer edge of the plate
      // itself... not inset" — so the raise carries through the rim round and cylinder,
      // and the plate's edge is genuinely thicker in the gaps. The trace agreed all
      // along (yellow spans 0.906 -> 1.0014R); an earlier build stopped it at rFlat.
      const q0 = raiseAt(d0);
      const q1 = raiseAt(d1);
      const B0 = b0 + e0, B1 = b1 + e1;
      const ringsAt = (B) => {
        const out = [];
        for (let j = 0; j <= rings; j++) out.push([B + (riseStart - B) * (j / rings), 0]);
        for (let k = 1; k < RISE.length; k++) out.push([RISE[k][0], RISE[k][1]]);
        for (let j = 1; j <= rings; j++) out.push([riseEnd + (rFlat - riseEnd) * (j / rings), params.raiseZoneHeight]);
        return out;
      };
      const L0 = ringsAt(B0);
      const L1 = ringsAt(B1);
      for (let k = 0; k < L0.length - 1; k++) {
        const zA0 = s * (HT + (q0 > 1e-9 ? L0[k][1] : 0));
        const zB0 = s * (HT + (q0 > 1e-9 ? L0[k + 1][1] : 0));
        const zA1 = s * (HT + (q1 > 1e-9 ? L1[k][1] : 0));
        const zB1 = s * (HT + (q1 > 1e-9 ? L1[k + 1][1] : 0));
        // Normals: the rise profile where the column is raised and mid-rise, else flat.
        //
        // EXCEPT at an arc END. There, one column is raised and its neighbour isn't, so
        // this quad IS the end wall — and giving it flat [0,0,s] normals lit it exactly
        // like the surfaces it joins, so it vanished into them (Ryan 2026-07-15: "they
        // blend in visually with the flat surfaces they meet up with"). A vertical wall
        // wants a TANGENTIAL normal; then it goes dark on its own at most angles, with no
        // material trick needed. push() gives every quad its own vertices, so this splits
        // the normal at the seam without touching the neighbours or opening a crack.
        // ONLY where the two columns actually sit at different heights. Testing merely
        // "one is raised" is not enough: at the bottom of the rise both are at height 0,
        // so that quad is genuinely FLAT — handing it a tangential normal made the
        // winding reference perpendicular to the geometry (g · ref ≈ 0), the test
        // collapsed to noise, and 6 triangles came out backfaced. Culled on one face they
        // showed background as white gaps; mirrored on the other they read black (Ryan
        // 2026-07-15). Same trap as the cutout tips: never decide winding from a normal
        // that is perpendicular to the surface it belongs to.
        const zStep = Math.abs((q0 > 1e-9 ? L0[k][1] : 0) - (q1 > 1e-9 ? L1[k][1] : 0));
        const isEnd = (q0 > 1e-9) !== (q1 > 1e-9) && zStep > 1e-4;
        const inRise = k >= rings && k < rings + RISE.length - 1;
        let nA0, nB0, nA1, nB1;
        if (isEnd) {
          const dw = q0 > 1e-9 ? d0 : d1;
          const sgn = q0 > 1e-9 ? 1 : -1; // face away from the raised side
          const nw = [-sgn * Math.sin(rad(dw)), sgn * Math.cos(rad(dw)), 0];
          nA0 = nB0 = nA1 = nB1 = nw;
        } else {
          nA0 = q0 > 1e-9 && inRise ? riseN(k - rings, s, d0) : [0, 0, s];
          nB0 = q0 > 1e-9 && inRise ? riseN(k - rings + 1, s, d0) : [0, 0, s];
          nA1 = q1 > 1e-9 && inRise ? riseN(k - rings, s, d1) : [0, 0, s];
          nB1 = q1 > 1e-9 && inRise ? riseN(k - rings + 1, s, d1) : [0, 0, s];
        }
        quadVN(
          'brand',
          [P(L0[k][0], d0, zA0), P(L0[k + 1][0], d0, zB0), P(L1[k + 1][0], d1, zB1), P(L1[k][0], d1, zA1)],
          [nA0, nB0, nB1, nA1],
          (pt) => brandUV(pt[0], pt[1])
        );
      }
    }
  }

  // ===========================================================================
  // 2. CUTOUT WALLS (body) — extruded along the band's perimeter. Where the band
  //    has zero width (solid material) nothing is emitted; where it tapers shut at
  //    ±26° the wall narrows to nothing, which IS the rounded end.
  // ===========================================================================
  const bandInner = (d) => band(d)[0] * R;
  const bandOuter = (d) => band(d)[1] * R;

  /**
   * Surface normal of a wall that follows r(θ) and is extruded along z.
   *
   * NOT the radial direction. Radial is correct only for a CYLINDER (dr/dθ = 0), and
   * the cutout wall is nothing like a cylinder near its ends — inner(θ) sweeps
   * 0.599R→0.817R across ~8°, so the true normal tilts up to ~78° away from radial
   * there. The code used to hardcode radial, which was ~0° wrong mid-cutout and 65-78°
   * wrong at the tips; the abrupt 4°→35° jump between them read as a hard seam crease
   * on the inside of the hand hole (Ryan 2026-07-15).
   *
   * For t(θ) = [r·cosθ, r·sinθ], the tangent is [r'cosθ − r·sinθ, r'sinθ + r·cosθ]
   * and the wall normal is tangent × ẑ. `sign` = +1 to face outward in r, −1 inward.
   */
  function wallNormal(rFn, d, sign) {
    const h = 0.02; // deg — finite difference for dr/dθ
    const rp = (rFn(d + h) - rFn(d - h)) / (2 * rad(h));
    const r = rFn(d);
    const c = Math.cos(rad(d));
    const s = Math.sin(rad(d));
    let nx = rp * s + r * c;
    let ny = -(rp * c - r * s);
    const L = Math.hypot(nx, ny) || 1;
    nx /= L;
    ny /= L;
    // Orient by the RADIAL component so both walls face away from their material,
    // regardless of which way the cross product happened to land.
    if ((nx * c + ny * s) * sign < 0) {
      nx = -nx;
      ny = -ny;
    }
    return [nx, ny, 0];
  }

  /**
   * Emits a quad with per-vertex normals, auto-winding against a reference.
   *
   * TAKES A uvFn, like quad() does. It used to hardcode CUV — fine for body/metal, which
   * are untextured, but the rim field was later rewritten to use this for its per-vertex
   * rise normals, and silently lost its planar UV with it. Every quad past 0.8167R then
   * sampled ONE texel from the texture's centre (bore = not printable) and rendered dark,
   * which read as the printable area stopping dead at the cutout tip radius (Ryan
   * 2026-07-15, the three red arrows). The zone was right all along; the geometry wasn't
   * reading it.
   */
  function quadVN(slot, p, n, uvFn) {
    const g = quadNormal(p); // from a non-degenerate triangle — see quadNormal
    if (!g) return;
    const rx = (n[0][0] + n[2][0]) / 2, ry = (n[0][1] + n[2][1]) / 2, rz = (n[0][2] + n[2][2]) / 2;
    const order = g[0] * rx + g[1] * ry + g[2] * rz >= 0 ? [0, 1, 2, 3] : [3, 2, 1, 0];
    push(
      slot,
      order.map((i) => ({ pos: p[i], normal: n[i], uv: uvFn ? uvFn(p[i]) : CUV }))
    );
  }

  /**
   * The rounded LIP of a cutout, each face: a quarter-round rolling from the flat face
   * down onto the wall. Mirrors the plate's outer rim treatment.
   *
   * Its normal sweeps from the face's [0,0,s] to the wall's true normal across the arc,
   * so the roll-over shades continuously into both neighbours rather than creasing at
   * either end.
   */
  function emitLip(rFn, d0, d1, r0, r1, sign) {
    const w0 = wallNormal(rFn, d0, sign);
    const w1 = wallNormal(rFn, d1, sign);
    const e0 = cutEdgeR(d0);
    const e1 = cutEdgeR(d1);
    if (e0 < 1e-6 && e1 < 1e-6) return;
    for (const s of [1, -1]) {
      for (let k = 0; k < eSeg; k++) {
        const A = (k / eSeg) * (Math.PI / 2);
        const B = ((k + 1) / eSeg) * (Math.PI / 2);
        // sign=+1 (inner wall): material is inward, so the face steps OUT to meet the
        // wall; sign=-1 (outer wall): the face steps IN. -sign captures both.
        const rr = (rBase, e, ang) => rBase - sign * e + sign * e * Math.sin(ang);
        // z MUST use the same per-column radius the r does. This read Math.max(e0, e1)
        // for z while r used e0/e1 — so wherever the lip radius tapers (i.e. at the
        // tips, where the band narrows) the lip's bottom edge and the wall's top edge,
        // which is at HT - cutEdgeR(d), landed at different heights and left a slit.
        // Four per handle: inner and outer wall, at each of the two tips (Ryan
        // 2026-07-15: "2 on either side (4 total)").
        const zz = (e, ang) => s * (HT - e + e * Math.cos(ang));
        const nAt = (w, ang) => {
          const nx = Math.sin(ang) * w[0];
          const ny = Math.sin(ang) * w[1];
          const nz = Math.cos(ang) * s;
          const L = Math.hypot(nx, ny, nz) || 1;
          return [nx / L, ny / L, nz / L];
        };
        quadVN(
          'body',
          [
            P(rr(r0, e0, A), d0, zz(e0, A)),
            P(rr(r0, e0, B), d0, zz(e0, B)),
            P(rr(r1, e1, B), d1, zz(e1, B)),
            P(rr(r1, e1, A), d1, zz(e1, A)),
          ],
          [nAt(w0, A), nAt(w0, B), nAt(w1, B), nAt(w1, A)]
        );
      }
    }
  }

  /** One wall segment, with PER-VERTEX normals so it shades smoothly across columns
   *  instead of faceting at every seam. Shortened by the lip radius at each face. */
  function emitWall(rFn, d0, d1, r0, r1, sign) {
    const n0 = wallNormal(rFn, d0, sign);
    const n1 = wallNormal(rFn, d1, sign);
    emitLip(rFn, d0, d1, r0, r1, sign);
    const h0 = HT - cutEdgeR(d0);
    const h1 = HT - cutEdgeR(d1);
    const p = [P(r0, d0, -h0), P(r0, d0, h0), P(r1, d1, h1), P(r1, d1, -h1)];
    const n = [n0, n0, n1, n1];
    // winding: reverse if the geometric normal opposes the intended one
    const ax = p[1][0] - p[0][0], ay = p[1][1] - p[0][1], az = p[1][2] - p[0][2];
    const bx = p[2][0] - p[0][0], by = p[2][1] - p[0][1], bz = p[2][2] - p[0][2];
    const gx = ay * bz - az * by, gy = az * bx - ax * bz, gz = ax * by - ay * bx;
    const ref = [(n0[0] + n1[0]) / 2, (n0[1] + n1[1]) / 2, 0];
    const order = gx * ref[0] + gy * ref[1] + gz * ref[2] >= 0 ? [0, 1, 2, 3] : [3, 2, 1, 0];
    push(
      'body',
      order.map((i) => ({ pos: p[i], normal: n[i], uv: CUV }))
    );
  }
  for (let i = 0; i < NC; i++) {
    const d0 = th(i);
    const d1 = th(i + 1);
    const [in0, out0] = band(d0);
    const [in1, out1] = band(d1);
    const w0 = out0 - in0;
    const w1 = out1 - in1;
    if (w0 < 1e-6 && w1 < 1e-6) continue; // solid here
    // Inner wall faces OUTWARD in r (into the hole); outer wall faces INWARD.
    // Normals come from wallNormal(), NOT from the radius direction — see its note.
    emitWall(bandInner, d0, d1, in0 * R, in1 * R, +1);
    emitWall(bandOuter, d0, d1, out0 * R, out1 * R, -1);
  }

  // ===========================================================================
  // 3. RIM (body) — rounded edge each side + the cylinder between.
  // ===========================================================================
  for (let i = 0; i < NC; i++) {
    const d0 = th(i);
    const d1 = th(i + 1);
    // The rim round + cylinder ride the LOCAL height, so they stand proud inside a
    // raised arc and sit flat across a cutout's span — "flush with the outer edge".
    const H0 = HT + raiseAt(d0);
    const H1 = HT + raiseAt(d1);
    for (const s of [1, -1]) {
      for (let k = 0; k < eSeg; k++) {
        const a0 = (k / eSeg) * (Math.PI / 2);
        const a1 = ((k + 1) / eSeg) * (Math.PI / 2);
        const r0 = rFlat + rimEr * Math.sin(a0);
        const r1 = rFlat + rimEr * Math.sin(a1);
        const z0 = s * (H0 - rimEr + rimEr * Math.cos(a0));
        const z1 = s * (H0 - rimEr + rimEr * Math.cos(a1));
        const z0b = s * (H1 - rimEr + rimEr * Math.cos(a0));
        const z1b = s * (H1 - rimEr + rimEr * Math.cos(a1));
        const am = (a0 + a1) / 2;
        const dm = (d0 + d1) / 2;
        // The 4th corner closes the ring back at (r0, z0). It previously read
        // `z1 === z0 ? z0 : z1`, which evaluates to z1 for every real segment — so
        // three of the quad's four corners sat at the same height and the rim round
        // was twisted, tearing white slivers through both faces' edges (Ryan
        // 2026-07-15: "weird little gaps/white lines/triangles ... on both sides").
        quad(
          'body',
          [P(r0, d0, z0), P(r1, d0, z1), P(r1, d1, z1b), P(r0, d1, z0b)],
          [Math.sin(am) * Math.cos(rad(dm)), Math.sin(am) * Math.sin(rad(dm)), s * Math.cos(am)]
        );
      }
    }
    const dm = (d0 + d1) / 2;
    quad(
      'body',
      [P(R, d0, -(H0 - rimEr)), P(R, d0, H0 - rimEr), P(R, d1, H1 - rimEr), P(R, d1, -(H1 - rimEr))],
      [Math.cos(rad(dm)), Math.sin(rad(dm)), 0]
    );
  }

  // ===========================================================================
  // 4. HUB BOSS (Ryan 2026-07-15) — metal + rubber share one flat top standing 1/4"
  //    proud of each face, then a rounded dip back down to the main face.
  //
  //    The dip is a COSINE ease, not a straight ramp or a pair of fillets: its slope
  //    is zero at BOTH ends by construction, so it leaves the hub top and meets the
  //    main face tangentially — "rounded edges and corners and all" falls out of the
  //    curve itself rather than needing separate round-overs bolted on each end.
  // ===========================================================================
  const boss = params.hubBossHeight;
  const rubberR = rubberRing;
  const rc = Math.min(params.hubCornerRadius, boss / 2.5);
  const zTop = HT + boss; // the RUBBER ring's top, 1/4" proud
  const zMetal = zTop - params.hubMetalRecess; // metal sits 0.5mm below it
  // 0.8, not 0.5: a half-width cap would silently clamp Ryan's 4.375 mm back to 3.63 and
  // quietly deliver ~45% more rounding where he asked for 75%. 0.8 still leaves a sliver
  // of flat so the hub can't become a pure torus.
  const bf = Math.min(params.hubBoreFillet, (collar - bore) * 0.8, zMetal - HT);

  /**
   * The dip, as an (r, height-above-the-face) polyline: round over the rubber's outer
   * edge, drop VERTICALLY, then fillet into the main face.
   *
   * Built as an explicit profile rather than an eased curve because the drop really is
   * a cliff — only its corners are rounded. Its radial run is therefore DERIVED (2 x rc),
   * not chosen, so the dip lands wherever the corner radius says it does.
   */
  const DIP = (() => {
    const N = 5;
    const pts = [];
    for (let k = 0; k <= N; k++) {
      const a = (k / N) * (Math.PI / 2); // convex round-over off the hub top
      pts.push([rubberR + rc * Math.sin(a), boss - rc + rc * Math.cos(a)]);
    }
    pts.push([rubberR + rc, rc]); // straight down the face of the drop
    for (let k = 0; k <= N; k++) {
      const a = Math.PI + (k / N) * (Math.PI / 2); // concave fillet onto the main face
      pts.push([rubberR + rc + rc + rc * Math.cos(a), rc + rc * Math.sin(a)]);
    }
    return pts;
  })();
  const dipR = rubberR + 2 * rc; // where it lands — derived, see above
  /** Outward normal at DIP[i], from the local tangent in the (r, height) plane. */
  const dipN = (i, s, d) => {
    const a = DIP[Math.max(0, i - 1)];
    const b = DIP[Math.min(DIP.length - 1, i + 1)];
    const dr = b[0] - a[0];
    const dz = b[1] - a[1];
    const L = Math.hypot(dz, dr) || 1;
    const nr = -dz / L;
    const nz = dr / L;
    return [nr * Math.cos(rad(d)), nr * Math.sin(rad(d)), s * nz];
  };

  for (let i = 0; i < NC; i++) {
    const d0 = th(i);
    const d1 = th(i + 1);
    const dm = (d0 + d1) / 2;
    for (const s of [1, -1]) {
      // The metal sits `recess` BELOW the rubber, so they are two planes with a small
      // step between them at r=collar — not the shared plane an earlier pass assumed.
      // ROUND-OVER from the bore up onto the flat metal: a=0 at the bore wall (vertical,
      // normal inward), a=90 on the flat (normal up). The normal sweeps between the two,
      // so the roll is continuous into both neighbours — "pretty soft curvy hubs".
      for (let k = 0; k < eSeg; k++) {
        const a0 = (k / eSeg) * (Math.PI / 2);
        const a1 = ((k + 1) / eSeg) * (Math.PI / 2);
        const rA = bore + bf - bf * Math.cos(a0);
        const rB = bore + bf - bf * Math.cos(a1);
        const zA = s * (zMetal - bf + bf * Math.sin(a0));
        const zB = s * (zMetal - bf + bf * Math.sin(a1));
        const nAt = (a, d) => [-Math.cos(a) * Math.cos(rad(d)), -Math.cos(a) * Math.sin(rad(d)), s * Math.sin(a)];
        quadVN(
          'metal',
          [P(rA, d0, zA), P(rB, d0, zB), P(rB, d1, zB), P(rA, d1, zA)],
          [nAt(a0, d0), nAt(a1, d0), nAt(a1, d1), nAt(a0, d1)]
        );
      }
      // metal flat top: past the fillet, out to the collar — recessed below the rubber
      quad('metal', [P(bore + bf, d0, s * zMetal), P(collar, d0, s * zMetal), P(collar, d1, s * zMetal), P(bore + bf, d1, s * zMetal)], [0, 0, s]);
      // the 0.5mm step up to the rubber: the rubber's inner wall, facing in over the metal
      quad('body', [P(collar, d0, s * zMetal), P(collar, d0, s * zTop), P(collar, d1, s * zTop), P(collar, d1, s * zMetal)], [-Math.cos(rad(dm)), -Math.sin(rad(dm)), 0]);
      // rubber top: collar -> rubber ring outer, at the FULL boss height
      quad('body', [P(collar, d0, s * zTop), P(rubberR, d0, s * zTop), P(rubberR, d1, s * zTop), P(collar, d1, s * zTop)], [0, 0, s]);
      // the dip down to the main face
      for (let k = 0; k < DIP.length - 1; k++) {
        const A = DIP[k], B = DIP[k + 1];
        quadVN(
          'body',
          [P(A[0], d0, s * (HT + A[1])), P(B[0], d0, s * (HT + B[1])), P(B[0], d1, s * (HT + B[1])), P(A[0], d1, s * (HT + A[1]))],
          [dipN(k, s, d0), dipN(k + 1, s, d0), dipN(k + 1, s, d1), dipN(k, s, d1)]
        );
      }
    }
    // bore wall — between the two round-overs, so it stops short of the metal's top face
    quad(
      'metal',
      [P(bore, d0, -(zMetal - bf)), P(bore, d0, zMetal - bf), P(bore, d1, zMetal - bf), P(bore, d1, -(zMetal - bf))],
      [-Math.cos(rad(dm)), -Math.sin(rad(dm)), 0] // faces inward
    );
  }

  const brandCount = idx.brand.length;
  const bodyCount = idx.body.length;
  const metalCount = idx.metal.length;
  const indices = [...idx.brand, ...idx.body, ...idx.metal];
  const groups = [
    { start: 0, count: brandCount, materialIndex: MATERIAL_BRAND_FACE, name: 'BrandFace' },
    { start: brandCount, count: bodyCount, materialIndex: MATERIAL_BODY, name: 'body' },
    { start: brandCount + bodyCount, count: metalCount, materialIndex: MATERIAL_METAL, name: 'metal' },
  ];

  return {
    positions,
    normals,
    uvs,
    colors,
    tangents,
    indices,
    groups,
    triangleCount: indices.length / 3,
    vertexCount: vertCount,
    brandVertexRange: { start: 0, count: (brandCount / 6) * 4 },
    diameter: 2 * R,
    thickness: 2 * HT,
    params,
  };
}

/**
 * THREE factory — mirrors createDumbbellGeometry(THREE, opts) so callers can swap.
 * @param {Partial<typeof DEFAULT_GRIP_PLATE_OPTS>} [opts]
 */
export function createGripPlateGeometry(THREE, opts = {}) {
  const mesh = buildGripPlateMesh(opts);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(mesh.positions, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(mesh.normals, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(mesh.uvs, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(mesh.colors, 3));
  g.setAttribute('tangent', new THREE.Float32BufferAttribute(mesh.tangents, 4));
  g.setIndex(mesh.indices);
  for (const gr of mesh.groups) g.addGroup(gr.start, gr.count, gr.materialIndex);
  g.userData.gripPlate = {
    weight: mesh.params.weight,
    diameter: mesh.diameter,
    thickness: mesh.thickness,
    triangleCount: mesh.triangleCount,
  };
  return g;
}
